


















































import CoIcon from '@/components/Atoms/co-icon/CoIcon.vue';
import CoText from '@/components/Atoms/co-text/CoText.vue';
import CoCard from '@/components/Molecules/co-card/CoCard.vue';
import axios from 'axios';
import EventBus from '@/eventBus';
import CoRoundButton from '@/components/Atoms/co-round-button/CoRoundButton.vue';
import { AttachmentFile, getAttachmentDownloadUrl, normalizeAttachmentFile } from '@/utils/attachment';
import { size } from 'lodash';

type ProvidedFile = AttachmentFile | null;

const MAX_FILE_SIZE_BYTES = 500 * 1024 * 1024; // 500 MB
const PLAN_FILE_LIMITS: Record<string, number> = {
    free: 5 * 1024 * 1024, // 5 MB
    starter: 25 * 1024 * 1024, // 20 MB
    professional: 100 * 1024 * 1024, // 100 MB
    business: 500 * 1024 * 1024, // 500 MB
};
const DOWNLOAD_QUERY_PARAM = 'downloadAttachmentId';

export default {
    name: 'CoFile',
    components: {
        CoCard,
        CoIcon,
        CoText,
        CoRoundButton,
    },
    props: {
        // The file object to be uploaded or displayed
        file: {
            type: Object as () => ProvidedFile,
            required: false,
            default: null,
        },
        mode: {
            type: String,
            default: 'upload',
            validator: (value: string) => ['upload', 'display'].includes(value),
        },
        size: {
            type: String,
            default: 'md',
            validator: (value: string) => ['ms', 'md'].includes(value),
        },
        parentUrl: {
            type: String,
            default: '',
        },
        showRemoveButton: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            // Local state for the file input
            localFile: null as ProvidedFile,
            uploading: false,
            uploadProgress: 0,
            fileSize: 0,
            uplodedFileURL: '',
            uploadedFileURL: '',
            downloading: false,
            downloadProgress: 0,
            handledDownloadQueryId: null as string | null,
        };
    },
    computed: {
        isDisplayMode(): boolean {
            return this.mode === 'display';
        },
        isUploadMode(): boolean {
            return !this.isDisplayMode;
        },
        attachmentFile(): AttachmentFile | null {
            return (this.isDisplayMode ? (this.localFile as AttachmentFile) : null) || null;
        },
        uploadAttachment(): AttachmentFile | null {
            return (this.isUploadMode ? (this.localFile as AttachmentFile) : null) || null;
        },
        fileName(): string {
            const file = this.isDisplayMode ? this.attachmentFile : this.uploadAttachment;
            return file?.Name || file?.file_name || '';
        },
        fileMeta(): string {
            if (!this.attachmentFile) return '';
            // const extension = this.attachmentFile.Extension || this.attachmentFile.format;
            const size = this.formatSize(this.attachmentFile.Size ?? this.attachmentFile.size);
            return [size].filter(Boolean).join(' • ');
        },
        downloadUrl(): string {
            const attachmentURL = this.attachmentFile?.URL || this.attachmentFile?.url || '';
            return getAttachmentDownloadUrl(attachmentURL);
        },
        fileId(): string {
            const file = this.attachmentFile || this.uploadAttachment;
            const id = file?.ID ?? file?.Id ?? file?.id ?? file?.URL ?? file?.url ?? '';
            return id ? String(id) : '';
        },
        showDownloadProgress(): boolean {
            return this.downloading && this.downloadProgress > 0;
        },
        isAdmin(): boolean {
            return !!this.$store?.state?.me?.Permissions?.includes('space_admin');
        },
        planSlug(): string {
            return (
                this.$store?.state?.space?.PlanSlug ||
                this.$store?.state?.space_configuration_admin?.PlanSlug ||
                ''
            )
                .toString()
                .toLowerCase();
        },
        maxFileSizeBytes(): number {
            return PLAN_FILE_LIMITS[this.planSlug] || MAX_FILE_SIZE_BYTES;
        },
    },
    watch: {
        file: {
            immediate: true,
            deep: true,
            handler(newFile: ProvidedFile, oldFile: ProvidedFile) {
                if (newFile !== oldFile) {
                    this.uploadedFileURL = '';
                    this.uplodedFileURL = '';
                }

                // file size
                this.fileSize = newFile?.Size || newFile?.size || 0;

                const normalizedFile = newFile ? normalizeAttachmentFile(newFile) : null;
                this.localFile = normalizedFile;

                if (this.isDisplayMode || !normalizedFile) {
                    this.uploadedFileURL = '';
                    this.uplodedFileURL = '';
                    return;
                }

                const uploadFile = this.uploadAttachment;
                if (!uploadFile) return;
                const isBlobURL = uploadFile.url && uploadFile.url.startsWith('blob:');

                if (!isBlobURL || !uploadFile.blob || this.uploadedFileURL) return;
                this.upload();
            },
        },
        attachmentFile() {
            this.maybeDownloadFromQuery();
        },
        $route() {
            this.maybeDownloadFromQuery();
        },
    },
    mounted() {
        this.maybeDownloadFromQuery();
    },
    methods: {
        async upload() {
            const uploadFile = this.uploadAttachment;
            if (!uploadFile || !uploadFile.blob) return;
            if (uploadFile.blob.size > this.maxFileSizeBytes) {
                if (this.isAdmin) {
                    EventBus.$emit('PAYWALL', {
                        feature: 'bigger-files',
                        plan: this.planSlug || 'unknown',
                    });
                } else {
                    EventBus.$emit('ERROR', {
                        Message: this.$t('messages.cofile.uploadTooLarge'),
                    });
                }
                this.$emit('remove');
                return;
            }
            this.uploading = true;
            this.uploadProgress = 0;
            const formData = new FormData();
            formData.append('file', uploadFile.blob, uploadFile.file_name || uploadFile.Name);
            formData.append('visibility', uploadFile.visibility || 'community');
            formData.append('format', uploadFile.format || uploadFile.Extension || '');
            formData.append('file_name', uploadFile.file_name || uploadFile.Name);
            formData.append('type', uploadFile.type || 'downloadable');
            // Calculate file size from blob
            this.fileSize = uploadFile.blob ? uploadFile.blob.size : 0;
            formData.append('size', this.fileSize.toString());
            this.$emit('uploading');

            try {
                await axios
                    .post(`${window.location.origin}/api/media/files`, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                        onUploadProgress: (progressEvent: ProgressEvent) => {
                            if (progressEvent.lengthComputable) {
                                this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            }
                        },
                    })
                    .then((response) => {
                        // Handle successful upload response
                        this.uplodedFileURL = response.data.url;
                        this.uploadedFileURL = response.data.url;
                        this.$emit('uploaded', response.data);
                    });
            } catch (error) {
                // Handle error
                this.uploadProgress = 0;
                EventBus.$emit('ERROR', {
                    Message: this.$t('messages.cofile.uploadFailed'),
                });
            } finally {
                this.uploading = false;
            }
        },
        maybeDownloadFromQuery() {
            if (!this.isDisplayMode || !this.downloadUrl || !this.fileId) return;
            const queryValue = this.$route?.query?.[DOWNLOAD_QUERY_PARAM];
            if (!queryValue) return;
            const normalized = String(queryValue);
            if (normalized !== this.fileId) return;
            if (this.handledDownloadQueryId === normalized) return;
            this.handledDownloadQueryId = normalized;
            this.downloadAttachment();
        },
        buildDownloadUrlWithParam() {
            if (!this.fileId) return '';
            const baseUrl = this.parentUrl;

            // remove host and origin from parentUrl
            let relativePath = baseUrl.replace(window.location.origin, '');
            // remove protocol and host from parentUrl
            relativePath = relativePath.replace(/^https?:\/\/[^\/]+/, '');
            const separator = relativePath.includes('?') ? '&' : '?';
            relativePath = `${relativePath}${separator}${DOWNLOAD_QUERY_PARAM}=${encodeURIComponent(this.fileId)}`;
            return relativePath;
        },

        async downloadAttachment() {
            if (!this.downloadUrl || this.downloading) return;
            if (this.$store?.state?.isInMobile && this.parentUrl && this.fileId) {
                // extract the file id from the download url
                const fileId = this.downloadUrl.match(/\/files\/([^\s/]+)\//i)[1];
                const redirectUrl = `/download/${fileId}?1=1`; // need to append a first query param because mobile app adds '&apptoken=...' automatically
                if (redirectUrl) {
                    const queryParam = {
                        ...this.$route.query,
                        m: 'mobile',
                        t: String(new Date().getTime()),
                        openinbrowser: redirectUrl,
                    };
                    this.$router.push({ query: queryParam });
                    return;
                }
            }
            this.downloading = true;
            this.downloadProgress = 0;
            try {
                const response = await axios.get(this.downloadUrl, {
                    responseType: 'blob',
                    onDownloadProgress: (progressEvent: ProgressEvent) => {
                        const total = progressEvent.lengthComputable
                            ? progressEvent.total
                            : this.fileSize || progressEvent.loaded;
                        if (total) {
                            this.downloadProgress = Math.round((progressEvent.loaded * 100) / total);
                        }
                    },
                });
                const blob = new Blob([response.data]);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', this.fileName || 'attachment');
                // link target to _blank to fix firefox download issue
                link.setAttribute('target', '_blank');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                EventBus.$emit('ERROR', { Message: this.$t('errors.failedGeneric') });
            } finally {
                this.downloading = false;
            }
        },
        formatSize(size?: number) {
            if (typeof size !== 'number' || Number.isNaN(size)) return '';
            if (size === 0) return '0 B';
            const units = ['B', 'KB', 'MB', 'GB', 'TB'];
            const index = Math.floor(Math.log(size) / Math.log(1024));
            const formatted = size / Math.pow(1024, index);
            return `${formatted.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
        },
    },
};
